export type HighlightKind = 'new' | 'improved' | 'fix';

export type ReleaseNoteHighlight = {
  title: string;
  description: string;
  href?: string;
  kind?: HighlightKind;
  section?: string;
};

export type ReleaseNote = {
  id: string;
  version: string;
  label?: string;
  date: string;
  title: string;
  summary: string;
  highlights: ReleaseNoteHighlight[];
};

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-09-19',
    version: '1.1.1',
    date: '2026-09-19',
    title: 'Workspaces, a Unified Home Screen & a Fresh New Look',
    summary:
      'Manage multiple brands with ease, see your whole business from one dashboard, and enjoy a cleaner, more intuitive design across the platform.',
    highlights: [
      {
        kind: 'new',
        section: 'Workspaces & Navigation',
        title: 'Workspaces',
        description:
          'Run separate brands, branches or client accounts side by side. Each workspace has its own WhatsApp number, contacts, team members and settings — switch between them from the sidebar.',
        href: '/home/workspaces',
      },
      {
        kind: 'new',
        section: 'Workspaces & Navigation',
        title: 'Unified Home Screen',
        description:
          'See revenue, orders, leads, conversations, campaigns, ads, posts and appointments in one place. Pick a date range, and the Needs Attention card shows you what to act on first.',
        href: '/home',
      },
      {
        kind: 'new',
        section: 'Workspaces & Navigation',
        title: 'Streamlined Design',
        description:
          'Every page now shares one consistent layout with a clear page header and easier-to-read text, so moving around the app feels familiar everywhere.',
      },
      {
        kind: 'new',
        section: 'CRM & Sales',
        title: 'Unified CRM Board',
        description:
          'Leads, Deals and Tickets now live together on one board. Switch between Board and Table views, filter in a click, and spot duplicate contacts before you import them.',
        href: '/home/boards/leads',
      },
      {
        kind: 'new',
        section: 'CRM & Sales',
        title: 'Payment Plans on Deals',
        description:
          'Split a deal into instalments with due dates and track what is paid, partly paid or overdue. Payment links can accept part payments, and you can set yourself a reminder to follow up on the balance.',
        href: '/home/boards/leads',
      },
      {
        kind: 'new',
        section: 'CRM & Sales',
        title: 'Call Recordings & Transcripts',
        description:
          'Play back a call from the lead’s Calls tab and read the transcript alongside it — the text follows the recording as it plays.',
        href: '/home/boards/leads',
      },
      {
        kind: 'new',
        section: 'Marketing, Social & Integrations',
        title: 'Campaigns & Templates in a Side Panel',
        description:
          'Create campaigns and WhatsApp message templates in a side panel without leaving your list. Browse ready-made templates with filters, and see the estimated cost before you send.',
        href: '/home/new-campaigns',
      },
      {
        kind: 'new',
        section: 'Marketing, Social & Integrations',
        title: 'Multi-Account Social Publishing',
        description:
          'Publish, schedule or save a draft for several Instagram and Facebook accounts at once, from a single post.',
        href: '/home/social-media/posts',
      },
      {
        kind: 'new',
        section: 'Marketing, Social & Integrations',
        title: 'Integrations Hub',
        description:
          'WhatsApp, social accounts, ad platforms, online stores and payment gateways each have their own page. Filter by connected or not connected, and set up a new connection in a few clicks.',
        href: '/home/integrations',
      },
      {
        kind: 'new',
        section: 'Marketing, Social & Integrations',
        title: 'WhatsApp Account Alerts',
        description:
          'A banner at the top of the app tells you when Meta places your WhatsApp account under review or limits it, and shows progress while your chat history syncs.',
      },
      {
        kind: 'new',
        section: 'Appointments & Commerce',
        title: 'Redesigned Appointments Calendar',
        description:
          'See every booking on one calendar, filter by team member, and reschedule or manage a booking from the side panel. Webinars appear as a single session showing how many seats are booked.',
        href: '/home/appointments-v2',
      },
      {
        kind: 'new',
        section: 'Appointments & Commerce',
        title: 'Commerce in One Place',
        description:
          'Manage products, categories, orders, invoices and abandoned carts from one screen, with your store overview and settings right alongside.',
        href: '/home/commerce/v2',
      },
    ],
  },
  {
    id: '2026-07-11',
    version: '1.1.0',
    date: '2026-07-11',
    title: 'Unified Inbox, CRM Exports & AI Reply Suggestions',
    summary:
      'One inbox for all your chat channels, background CRM exports, deal payments, AI reply suggestions for Instagram and Facebook, and post publishing notifications.',
    highlights: [
      {
        kind: 'improved',
        title: 'Unified Channels Inbox',
        description:
          'WhatsApp, Instagram and Facebook share one cleaner inbox — assign agents, set priority, filter by tags or campaigns, and pick templates from a side panel.',
        href: '/home/inbox',
      },
      {
        kind: 'new',
        title: 'Background CRM Exports',
        description:
          'Export your leads and customers to Excel or CSV in the background — you get a notification as soon as the file is ready.',
        href: '/home/leads',
      },
      {
        kind: 'new',
        title: 'Deals & Razorpay Subscription Links',
        description:
          'Manage deal payments, CRM plans and Razorpay subscription links, including offers and payment progress.',
        href: '/home/deals',
      },
      {
        kind: 'new',
        title: 'AI Reply Suggestions for Instagram & Facebook',
        description:
          'Get AI-suggested replies inside Instagram and Facebook conversations so your team can respond faster.',
        href: '/home/instagram/inbox',
      },
      {
        kind: 'new',
        title: 'Post Publishing Notifications',
        description:
          'Get notified when a scheduled Instagram or Facebook post goes live — or if it fails to publish.',
        href: '/home/instagram/posts',
      },
    ],
  },
];

export const WHATS_NEW_SEEN_PREF_KEY = 'whats_new_seen_id';
export const WHATS_NEW_SEEN_KEY = 'wenext_whats_new_seen_id';
export const WHATS_NEW_SEEN_EVENT = 'wenext:whats-new-seen';

const SEMVER_RE = /^\d+\.\d+\.\d+$/;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike {
  if (typeof localStorage === 'undefined') {
    return { getItem: () => null, setItem: () => undefined };
  }
  return localStorage;
}

export function whatsNewSeenStorageKey(userId?: string | null): string {
  const id =
    userId != null && String(userId).trim() ? String(userId).trim() : 'anonymous';
  return `${WHATS_NEW_SEEN_KEY}:${id}`;
}

export function getLatestRelease(): ReleaseNote | null {
  return RELEASE_NOTES[0] ?? null;
}

export function getCurrentAppVersion(): string {
  return getLatestRelease()?.version ?? '0.0.0';
}

export function formatVersion(version: string): string {
  const trimmed = (version || '').trim();
  if (!trimmed) return 'v0.0.0';
  return trimmed.startsWith('v') || trimmed.startsWith('V')
    ? `v${trimmed.slice(1)}`
    : `v${trimmed}`;
}

export function isValidSemVer(version: string): boolean {
  return SEMVER_RE.test((version || '').trim());
}

export function highlightKindLabel(kind?: HighlightKind): string {
  switch (kind) {
    case 'improved':
      return 'Improved';
    case 'fix':
      return 'Fix';
    case 'new':
    default:
      return 'New';
  }
}

function emitSeenChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(WHATS_NEW_SEEN_EVENT));
  } catch {
  }
}

export function getWhatsNewSeenIdFromPreferences(
  preferences: unknown,
): string | null {
  if (!preferences) return null;
  let prefs = preferences;
  if (typeof prefs === 'string') {
    try {
      prefs = JSON.parse(prefs);
    } catch {
      return null;
    }
  }
  if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return null;
  const raw = (prefs as Record<string, unknown>)[WHATS_NEW_SEEN_PREF_KEY];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed || null;
}

export function syncWhatsNewSeenFromPreferences(
  userId: string | null | undefined,
  preferences: unknown,
  storage: StorageLike = defaultStorage(),
): string | null {
  const fromServer = getWhatsNewSeenIdFromPreferences(preferences);
  if (!fromServer || !userId) return fromServer;
  try {
    const key = whatsNewSeenStorageKey(userId);
    const local = storage.getItem(key);
    if (local !== fromServer) {
      storage.setItem(key, fromServer);
      emitSeenChanged();
    }
  } catch {
  }
  return fromServer;
}

export function getSeenReleaseId(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): string | null {
  try {
    const key = whatsNewSeenStorageKey(userId);
    let raw = storage.getItem(key);
    if (!raw || !raw.trim()) {
      const legacy = storage.getItem(WHATS_NEW_SEEN_KEY);
      if (legacy && legacy.trim()) {
        raw = legacy.trim();
        try {
          storage.setItem(key, raw);
        } catch {
        }
      }
    }
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

export function markReleaseSeen(
  id: string,
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): void {
  if (!id) return;
  try {
    const key = whatsNewSeenStorageKey(userId);
    if (storage.getItem(key) === id) return;
    storage.setItem(key, id);
    emitSeenChanged();
  } catch {
  }
}

export async function markReleaseSeenPersistent(
  id: string,
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): Promise<boolean> {
  if (!id) return false;
  markReleaseSeen(id, userId, storage);

  try {
    const { markWhatsNewSeenOnServer } = await import('@/lib/api/services/whatsNew');
    const result = await markWhatsNewSeenOnServer(id);
    if (result.ok) {
      const seenId = result.whats_new_seen_id ?? id;
      markReleaseSeen(seenId, userId, storage);
      try {
        const raw = storage.getItem('user');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            const prefs =
              parsed.preferences && typeof parsed.preferences === 'object'
                ? { ...parsed.preferences }
                : {};
            prefs[WHATS_NEW_SEEN_PREF_KEY] = seenId;
            parsed.preferences = prefs;
            storage.setItem('user', JSON.stringify(parsed));
          }
        }
      } catch {
      }
    }
    return result.ok;
  } catch {
    return false;
  }
}

export function markLatestReleaseSeen(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): void {
  const latest = getLatestRelease();
  if (!latest) return;
  markReleaseSeen(latest.id, userId, storage);
}

export async function markLatestReleaseSeenPersistent(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): Promise<boolean> {
  const latest = getLatestRelease();
  if (!latest) return false;
  return markReleaseSeenPersistent(latest.id, userId, storage);
}

export function shouldShowWhatsNewModal(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
  preferences?: unknown,
): boolean {
  const latest = getLatestRelease();
  if (!latest) return false;
  const fromPrefs = getWhatsNewSeenIdFromPreferences(preferences);
  if (fromPrefs) {
    if (userId) syncWhatsNewSeenFromPreferences(userId, preferences, storage);
    return fromPrefs !== latest.id;
  }
  return getSeenReleaseId(userId, storage) !== latest.id;
}

export function formatReleaseDate(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
